import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Loader2, Lock, User } from 'lucide-react'
import toast from 'react-hot-toast'
import { apiErrorDetail } from '@/lib/api/client'
import { invitationApi, type PublicInvitation } from '@/lib/api/invitations'
const SithabileLogo = '/sithabile-fingerprint-logo.png'

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; invite: PublicInvitation }
  | { kind: 'expired' }
  | { kind: 'invalid' }
  /** The lookup itself failed (server or network); retrying may still work. */
  | { kind: 'failed' }
  /** The invited email already has an account, so there is nothing to create. */
  | { kind: 'exists' }

interface PublicAuthProvider {
  id: string
  name: string
  provider_type: string
}

const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/
const PASSWORD_HINT = 'At least 8 characters with upper and lower case letters and a digit'
const inputClass =
  'w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#5869ff] focus:border-transparent'

/** Formats an invitation expiry timestamp for display, or `null` if unparsable. */
function formatExpiry(value: string): string | null {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString(undefined, { dateStyle: 'long' })
}

/** Reads an HTTP status off an unknown API rejection without widening to `any`. */
function errorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status
}

/**
 * Public page behind an invitation link (`/invite/:token`).
 *
 * Looks the token up to show who was invited and with which role, then creates
 * the account from a name and password. Accepting does not start a session, so
 * the invitee is sent to `/login?invited=1` to sign in; organisation SSO is
 * offered as an alternative when providers are configured.
 *
 * A dead link (used, revoked, expired) is reported as such, while a failed
 * lookup — a server error or a network blip — offers a retry instead, so a
 * valid invitation is never written off as broken.
 */
export function AcceptInvitePage() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [form, setForm] = useState({ first_name: '', last_name: '', password: '', confirm: '' })
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [providers, setProviders] = useState<PublicAuthProvider[]>([])
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setState({ kind: 'loading' })

    invitationApi
      .lookup(token)
      .then((invite) => {
        if (!cancelled) setState({ kind: 'ready', invite })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const status = errorStatus(err)
        if (status === 410) setState({ kind: 'expired' })
        else if (status === 404 || status === 403) setState({ kind: 'invalid' })
        else setState({ kind: 'failed' })
      })

    return () => {
      cancelled = true
    }
  }, [token, reloadKey])

  useEffect(() => {
    let cancelled = false

    void (async () => {
      try {
        const response = await fetch('/api/v1/auth-providers/public')
        if (!response.ok) return
        const list = await response.json()
        if (!cancelled && Array.isArray(list)) setProviders(list)
      } catch (error) {
        // SSO buttons are optional; a failed lookup simply hides them.
        console.warn('Failed to load auth providers', error)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  const update =
    (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) =>
      setForm((previous) => ({ ...previous, [field]: event.target.value }))

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    const firstName = form.first_name.trim()
    const lastName = form.last_name.trim()
    if (!firstName || !lastName) {
      setError('Please enter your first and last name')
      return
    }
    if (form.password !== form.confirm) {
      setError('Passwords do not match')
      return
    }
    if (!PASSWORD_RULE.test(form.password)) {
      setError(
        'Password must be at least 8 characters with upper and lower case letters and a digit'
      )
      return
    }

    setSubmitting(true)
    try {
      await invitationApi.accept(token, {
        first_name: firstName,
        last_name: lastName,
        password: form.password,
      })
      toast.success('Account created — sign in to continue')
      navigate('/login?invited=1', { replace: true })
    } catch (err: unknown) {
      const status = errorStatus(err)
      if (status === 410) setState({ kind: 'expired' })
      else if (status === 404) setState({ kind: 'invalid' })
      else if (status === 409) setState({ kind: 'exists' })
      else setError(apiErrorDetail(err) || 'Could not create your account. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const startSso = async (provider: PublicAuthProvider) => {
    try {
      const response = await fetch(`/api/v1/auth/sso/${provider.provider_type}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ provider_id: provider.id }),
      })
      const data = await response.json()
      if (data?.auth_url) window.location.href = data.auth_url
      else toast.error('SSO not configured properly')
    } catch {
      toast.error('Failed to start SSO sign-in')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#000742] px-4">
      <div className="w-full max-w-md bg-white/90 dark:bg-[#000742]/90 rounded-2xl shadow-2xl p-8 border border-white/20 dark:border-[#5869ff]/20">
        <div className="text-center mb-6">
          <img src={SithabileLogo} alt="Sithabile" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#000742] dark:text-white">NetApp Dashboard</h1>
        </div>

        {state.kind === 'loading' && (
          <p role="status" className="text-center text-gray-600 dark:text-gray-400">
            Checking your invitation…
          </p>
        )}

        {state.kind === 'expired' && (
          <div className="text-center space-y-3">
            <p className="text-gray-900 dark:text-white font-medium">
              This invitation has expired.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Ask the administrator who invited you to send a new link.
            </p>
          </div>
        )}

        {state.kind === 'invalid' && (
          <div className="text-center space-y-3">
            <p className="text-gray-900 dark:text-white font-medium">
              This invitation link is no longer valid.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              It may have been used already or revoked. If you already have an account,{' '}
              <Link to="/login" className="text-[#5869ff] underline">
                sign in
              </Link>
              .
            </p>
          </div>
        )}

        {state.kind === 'failed' && (
          <div className="text-center space-y-3">
            <p className="text-gray-900 dark:text-white font-medium">
              We couldn&apos;t check your invitation.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              This may be a temporary problem.
            </p>
            <button
              type="button"
              onClick={() => setReloadKey((previous) => previous + 1)}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#5869ff] to-[#000742] text-white font-medium hover:shadow-lg"
            >
              Try again
            </button>
          </div>
        )}

        {state.kind === 'exists' && (
          <div className="text-center space-y-3">
            <p className="text-gray-900 dark:text-white font-medium">
              An account already exists for this email.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <Link to="/login" className="text-[#5869ff] underline">
                Sign in instead.
              </Link>
            </p>
          </div>
        )}

        {state.kind === 'ready' && (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              You&apos;ve been invited as{' '}
              <span className="font-medium capitalize text-gray-900 dark:text-white">
                {state.invite.role.replace('_', ' ')}
              </span>{' '}
              for{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {state.invite.email}
              </span>
              .
            </p>
            {formatExpiry(state.invite.expires_at) && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                This link expires on {formatExpiry(state.invite.expires_at)}.
              </p>
            )}

            {/* Real but hidden, so password managers save the invited address as the username. */}
            <input
              type="email"
              value={state.invite.email}
              readOnly
              autoComplete="username"
              className="hidden"
              aria-hidden="true"
              tabIndex={-1}
            />

            {error && (
              <div
                role="alert"
                className="p-3 rounded-lg bg-red-50 dark:bg-red-500/10 text-sm text-red-700 dark:text-red-300"
              >
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="first-name"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  First name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="first-name"
                    autoFocus
                    required
                    autoComplete="given-name"
                    value={form.first_name}
                    onChange={update('first_name')}
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="last-name"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Last name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="last-name"
                    required
                    autoComplete="family-name"
                    value={form.last_name}
                    onChange={update('last_name')}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  type="password"
                  required
                  maxLength={100}
                  autoComplete="new-password"
                  aria-describedby="password-hint"
                  value={form.password}
                  onChange={update('password')}
                  className={inputClass}
                  placeholder="8+ characters, upper, lower and a digit"
                />
              </div>
              <p id="password-hint" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {PASSWORD_HINT}
              </p>
            </div>

            <div>
              <label
                htmlFor="confirm"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Confirm password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="confirm"
                  type="password"
                  required
                  maxLength={100}
                  autoComplete="new-password"
                  value={form.confirm}
                  onChange={update('confirm')}
                  className={inputClass}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white font-medium rounded-lg hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Create account
            </button>

            {providers.length > 0 && (
              <div className="pt-2 space-y-2">
                <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                  or use your organisation sign-in with the same email
                </p>
                {providers.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => void startSso(provider)}
                    className="w-full py-2.5 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg text-[#344054] dark:text-gray-300 hover:bg-[#5869ff]/10 transition-colors"
                  >
                    Continue with {provider.name}
                  </button>
                ))}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  )
}
